import pluralize from 'pluralize';
import { useCallback, useState } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Board, type Clip } from '@air/api/types';
import { XIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';

import { useCreatePrivateAssetBoardMembershipUpdateTask } from '~/components/AssetBoardMembershipUpdateTasks/useCreatePrivateAssetBoardMembershipUpdateTask';
import {
  type BoardSearchOption,
  PrivateBoardSearch,
  type PrivateBoardSearchProps,
} from '~/components/BoardSearch/PrivateBoardSearch';
import BoardThumbnail from '~/components/BoardThumbnail/BoardThumbnail';
import { ListSectionHeader } from '~/components/Shared/ListSectionHeader';
import { ToastLink } from '~/components/UI/ToastLink';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useGoToPrivateBoardPage } from '~/hooks/useGoToPrivateBoardPage';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { useAddAssetsToBoards } from '~/swr-hooks/boards/useAddAssetsToBoards';
import { canAddAssetsToBoard } from '~/utils/permissions/boardPermissions';

const MAX_SELECT_ALL_TARGET_BOARDS = 10;

export type AddClipsToBoardModalProps = {
  clipIds?: Clip['id'][];
  trackLocation: AirActionTrackingLocation;
  onSubmit?: () => void;
  parentBoardId?: Board['id'];
  selectAll?: {
    boardId: Board['id'];
    includeDescendants: boolean;
    totalSelectedItemsCount: number;
  };
};

const getInstructions = (isPlural: boolean) =>
  isPlural
    ? 'The assets will appear on a new board (or boards) and will not be removed from their current locations.'
    : 'The asset will appear on a new board (or boards) and will not be removed from its current location.';

export const AddAssetsToBoardsModal = ({
  clipIds = [],
  onClose,
  trackLocation,
  onSubmit,
  parentBoardId,
  selectAll,
}: AirModalProps<AddClipsToBoardModalProps>) => {
  const { goToBoardPage } = useGoToPrivateBoardPage();
  const { showToast } = useToasts();
  const [adding, setAdding] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [selectedBoards, setSelectedBoards] = useState<BoardSearchOption[]>([]);
  const isBoardSelected = useCallback(
    (board: Pick<Board, 'id'>) => {
      return selectedBoards.some((b) => b.id === board.id);
    },
    [selectedBoards],
  );
  const { getBoardPermissions } = useBoardPermissionsCache();

  const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
  const addAssetsToBoardMutation = useAddAssetsToBoards();
  const { createPrivateAssetBoardMembershipUpdateTask } = useCreatePrivateAssetBoardMembershipUpdateTask();
  const itemCount = selectAll?.totalSelectedItemsCount ?? clipIds.length;
  const isSelectAll = !!selectAll;

  const onBoardSelectChange: PrivateBoardSearchProps['onBoardSelectChange'] = useCallback(
    ({ board, selected }) => {
      setSelectedBoards((current) => {
        if (selected) {
          if (isSelectAll && current.length >= MAX_SELECT_ALL_TARGET_BOARDS) {
            return current;
          }

          return [...current, board];
        }

        return current.filter((b) => b.id !== board.id);
      });
    },
    [isSelectAll],
  );

  const getIsBoardDisabled: Required<PrivateBoardSearchProps>['getIsBoardDisabled'] = useCallback(
    (board) => {
      const boardPermissions = getBoardPermissions(board.id);

      if (!canAddAssetsToBoard(boardPermissions)) {
        return {
          isDisabled: true,
          message: 'You do not have permission to add assets to this board',
        };
      }

      if (isSelectAll && selectedBoards.length >= MAX_SELECT_ALL_TARGET_BOARDS && !isBoardSelected(board)) {
        return {
          isDisabled: true,
          message: `You can select up to ${MAX_SELECT_ALL_TARGET_BOARDS} boards`,
        };
      }
    },
    [getBoardPermissions, isBoardSelected, isSelectAll, selectedBoards.length],
  );

  if (showingSubscriptionExpiredModal) {
    return null;
  }

  const goToBoard = (board: Pick<Board, 'id' | 'title'>) => {
    // biome-ignore lint/nursery/noFloatingPromises: PLA-1724 — pre-existing debt, not introduced by the icon migration
    goToBoardPage({ board, trackLocation: 'add-clips-to-board-toast' });
  };

  // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
  const handleAddClick = async () => {
    const boards = selectedBoards;

    if (selectAll && boards.length > MAX_SELECT_ALL_TARGET_BOARDS) {
      showToast(`You can select up to ${MAX_SELECT_ALL_TARGET_BOARDS} boards`);
      return;
    }

    setAdding(true);

    try {
      if (selectAll) {
        const result = await createPrivateAssetBoardMembershipUpdateTask({
          boardId: selectAll.boardId,
          includeDescendants: selectAll.includeDescendants,
          operation: {
            type: 'add',
            targetBoards: boards,
          },
          totalSelectedItemsCount: selectAll.totalSelectedItemsCount,
          trackLocation,
        });

        // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
        if (result.error) {
          throw result.error;
        }

        onSubmit?.();
        onClose();
        return;
      }

      await addAssetsToBoardMutation.mutateAsync({
        clipIds,
        boards,
        trackLocation,
        parentBoardId,
      });

      onSubmit?.();
      onClose();
      showToast(
        <>
          {`${pluralize('item', itemCount, true)} added to `}
          {boards.length === 1 ? (
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            <ToastLink onClick={() => goToBoard(boards[0])}>{boards[0].title}</ToastLink>
          ) : (
            <>multiple boards</>
          )}
        </>,
      );
    } catch (_error) {
      setAdding(false);
      showToast('An error occurred - please try again later');
    }
  };

  return (
    <Modal isOpen onDismiss={onClose} data-testid="ADD_ASSETS_TO_BOARDS_MODAL">
      <div className="flex flex-col gap-4">
        <header className="flex justify-between gap-4">
          <ModalTitle>Add to…</ModalTitle>
          <ModalCloseButton className="shrink-0" onClick={onClose} />
        </header>
        <p className="ads:text-metadata text-14 text-gray-10">{getInstructions(itemCount > 1)}</p>
        <PrivateBoardSearch
          className="mt-3 [&>.workspace-boards]:h-[240px]"
          isBoardSelected={isBoardSelected}
          onBoardSelectChange={onBoardSelectChange}
          shouldExpandLibraryInsteadOfSelect={true}
          getIsBoardDisabled={getIsBoardDisabled}
        />
        <ListSectionHeader
          className="mb-0 pb-0"
          showDowntick="always"
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
          onClick={() => setShowSummary(!showSummary)}
          collapsed={!showSummary}
          headerTitle={<>Selected boards ({selectedBoards.length})</>}
        />
        {showSummary && (
          <div className="flex flex-col gap-2">
            {selectedBoards.map((option, index) => (
              <div
                className="flex items-center justify-between rounded-4 ads:bg-subtle bg-gray-3 px-3 py-2"
                key={index}
              >
                <div className="flex items-center gap-2">
                  <BoardThumbnail board={option} sizes="32px" width={32} height={32} />
                  <div>
                    <div className="font-medium ads:text-secondary text-14 text-gray-11">{option.title}</div>
                    <div className="ads:text-metadata text-12 text-gray-10">
                      {option.ancestors?.map((a) => a.title).join(' / ') || 'Workspace Boards'}
                    </div>
                  </div>
                </div>
                <IconButton
                  adsIcon
                  appearance="ghost"
                  color="gray"
                  size="extra-small"
                  icon={XIcon}
                  data-testid="ADD_ASSETS_TO_BOARDS_MODAL_CLEAR"
                  label="Clear"
                  // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                  onClick={() => setSelectedBoards((boards) => boards.filter((b) => b.id !== option.id))}
                />
              </div>
            ))}
          </div>
        )}

        <footer className="flex justify-end gap-3">
          <Button
            appearance="ghost"
            color="gray"
            onClick={onClose}
            size="large"
            data-testid="ADD_ASSETS_TO_BOARDS_MODAL_CANCEL"
          >
            Cancel
          </Button>
          <Button
            data-testid="ADD_ASSETS_TO_BOARDS_MODAL_SUBMIT"
            onClick={handleAddClick}
            isLoading={adding}
            disabled={adding || !selectedBoards.length}
            appearance="filled"
            color="blue"
            size="large"
          >
            {adding
              ? 'Adding…'
              : `Add to ${selectedBoards.length >= 1 ? pluralize('board', selectedBoards.length, true) : 'board'}`}
          </Button>
        </footer>
      </div>
    </Modal>
  );
};
